import React from 'react';
import { MapPin, Clock, Star, CheckCircle, Edit, Trash2, XCircle, TrendingUp } from 'lucide-react';
import { useEventImage } from '../hooks/useEventThumbnail';
import { goToLogin } from '../utils/navigation';

interface EventThumbnail {
  base64: string;
  mimeType: string;
}

interface EventCardProps {
  event: {
    id: string | number;
    title: string;
    description?: string;
    location?: string;
    date?: string;
    // All possible image fields
    imageUrl?: string;
    IMAGE_URL?: string;
    thumbnailBase64?: string;
    thumbnailMimeType?: string;
    thumbnail?: EventThumbnail;
    images?: string[];
    IMAGES?: string[];
    mimeType?: string;
    // Status fields
    isFeatured?: boolean;
    isTrending?: boolean;
    isPopular?: boolean;
    status?: string;
    isVerified?: boolean;
    isActive?: boolean;
    entryFee?: string;
    // Original event data
    originalEvent?: any;
  };
  onViewDetails: (event: any) => void;
  onEdit?: (event: any) => void;
  canEdit?: boolean;
  // Authentication-related props
  user?: any; // User object from auth context
  // Admin-specific props
  isAdmin?: boolean;
  onApprove?: (event: any) => void;
  onReject?: (event: any) => void;
  onFeature?: (event: any) => void;
  onUnfeature?: (event: any) => void;
  onTrend?: (event: any) => void;
  onUntrend?: (event: any) => void;
  onDelete?: (event: any) => void;
}

const EventCard: React.FC<EventCardProps> = ({ 
  event, 
  onViewDetails, 
  onEdit, 
  canEdit = false,
  user,
  isAdmin = false,
  onApprove,
  onReject,
  onFeature,
  onUnfeature,
  onTrend,
  onUntrend,
  onDelete
}) => {
  // Get the appropriate image source with better fallback handling
  // Component for rendering event image with thumbnail API support
  const EventImage: React.FC = () => {
    const eventId = typeof event.id === 'number' ? event.id : parseInt(event.id.toString());
    const originalEvent = event.originalEvent || event;
    const { thumbnailUrl, loading } = useEventImage(originalEvent, eventId);
    
    if (loading) {
      return (
        <div className="w-full h-72 bg-gray-200 animate-pulse flex items-center justify-center">
          <div className="text-gray-400 text-sm">Loading...</div>
        </div>
      );
    }
    
    return (
      <img
        src={thumbnailUrl || 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=400'}
        alt={event.title}
        className="w-full h-72 object-contain"
        onError={(e) => {
          console.error('❌ Image failed to load for event:', event.title);
          e.currentTarget.src = 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=400';
        }}
      />
    );
  };

  // Handle click on the card - check authentication first
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Check if user is logged in (for admin dashboards, user is always present)
    if (!user && !isAdmin) {
      // User is not logged in, redirect to login page
      goToLogin();
      return;
    }
    
    // User is logged in or this is an admin dashboard, proceed to event details
    onViewDetails(event);
  };

  return (
    <div 
      key={event.id} 
      className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-200 flex flex-col h-full"
    >
      {/* Event Thumbnail */}
      <div className="relative h-72 overflow-hidden bg-gray-100">
        <EventImage />
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          {event.isFeatured && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              <Star className="h-3 w-3 mr-1" />
              Featured
            </span>
          )}
          {event.isTrending && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              <TrendingUp className="h-3 w-3 mr-1" />
              Trending
            </span>
          )}
          {event.isPopular && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" />
              Popular
            </span>
          )}
          {event.status === 'approved' && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" />
              Approved
            </span>
          )}
          {(event.isVerified === true && !event.status) && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" />
              Verified
            </span>
          )}
        </div>
      </div>
      
      {/* Event Details */}
      <div className="p-4 flex flex-col flex-1">
        <h4 className="font-medium text-gray-900 mb-2">{event.title}</h4>
        <p className="text-sm text-gray-600 mb-3 line-clamp-2 flex-1">{event.description || 'No description available'}</p>
        <div className="flex items-center space-x-2 mb-2">
          <MapPin className="h-4 w-4 text-gray-400" />
          <span className="text-xs text-gray-500">{event.location || 'Location not set'}</span>
        </div>
        <div className="flex items-center space-x-2 mb-3">
          <Clock className="h-4 w-4 text-gray-400" />
          <span className="text-xs text-gray-500">{event.date || 'Date not set'}</span>
        </div>
        {isAdmin ? (
          // Admin action buttons
          <div className="space-y-2 mt-auto">
            {/* Top row - Approve/Reject and Feature */}
            <div className="flex justify-between items-center space-x-1">
              <div className="flex space-x-1">
                {event.status !== 'approved' && event.isVerified !== true && onApprove && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onApprove(event);
                    }}
                    className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200 transition-colors duration-200 flex items-center space-x-1"
                    title="Approve Event"
                  >
                    <CheckCircle className="h-3 w-3" />
                    <span>Approve</span>
                  </button>
                )}
                {(event.status === 'approved' || event.isVerified === true) && onReject && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onReject(event);
                    }}
                    className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200 transition-colors duration-200 flex items-center space-x-1"
                    title="Revoke Approval"
                  >
                    <XCircle className="h-3 w-3" />
                    <span>Revoke</span>
                  </button>
                )}
              </div>
              
              <div className="flex space-x-1">
                {!event.isFeatured && onFeature && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onFeature(event);
                    }}
                    className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded hover:bg-yellow-200 transition-colors duration-200 flex items-center space-x-1"
                    title="Feature Event"
                  >
                    <Star className="h-3 w-3" />
                    <span>Feature</span>
                  </button>
                )}
                {event.isFeatured && onUnfeature && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onUnfeature(event);
                    }}
                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition-colors duration-200 flex items-center space-x-1"
                    title="Unfeature Event"
                  >
                    <Star className="h-3 w-3 fill-current" />
                    <span>Unfeature</span>
                  </button>
                )}
                
                {!event.isTrending && onTrend && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onTrend(event);
                    }}
                    className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200 transition-colors duration-200 flex items-center space-x-1"
                    title="Mark as Trending"
                  >
                    <TrendingUp className="h-3 w-3" />
                    <span>Trend</span>
                  </button>
                )}
                {event.isTrending && onUntrend && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onUntrend(event);
                    }}
                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition-colors duration-200 flex items-center space-x-1"
                    title="Remove from Trending"
                  >
                    <TrendingUp className="h-3 w-3 fill-current" />
                    <span>Untrend</span>
                  </button>
                )}
              </div>
            </div>
            
            {/* Bottom row - Edit, View Details, Delete */}
            <div className="flex justify-between items-center space-x-1">
              <div className="flex space-x-1">
                {canEdit && onEdit && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(event);
                    }}
                    className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 transition-colors duration-200 flex items-center space-x-1"
                  >
                    <Edit className="h-3 w-3" />
                    <span>Edit</span>
                  </button>
                )}
                <button 
                  onClick={handleClick}
                  className="px-2 py-1 bg-dpn-pink text-white text-xs rounded hover:bg-dpn-raspberry transition-colors duration-200"
                >
                  View Details
                </button>
              </div>
              
              {onDelete && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
                      onDelete(event);
                    }
                  }}
                  className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200 transition-colors duration-200 flex items-center space-x-1"
                  title="Delete Event"
                >
                  <Trash2 className="h-3 w-3" />
                  <span>Delete</span>
                </button>
              )}
            </div>
          </div>
        ) : (
          // Regular user buttons
          <div className="flex justify-end items-center space-x-2 mt-auto">
            {canEdit && onEdit && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(event);
                }}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-1"
              >
                <Edit className="h-3 w-3" />
                <span>Edit</span>
              </button>
            )}
            <button 
              onClick={handleClick}
              className="px-3 py-1 bg-dpn-pink text-white text-sm rounded-md hover:bg-dpn-raspberry transition-colors duration-200"
            >
              View Details
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCard;
